import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { apiPayloadCreator } from "util/api";

const AUTH_REFRESH_INTERVAL = 3600 * 1000;

// Structure of the authentication state.

// (1) authenticationStatus: one of the following values

// No authentication check or login attempt has been made yet:
const AUTH_UNINITIALIZED = null;

// An attempt is underway to verify a session cookie or token retrieved from
// session storage:
const AUTH_CHECKING = "checking";

// An attempt to verify a session cookie or token was unsuccessful, or a login
// attempt was unsuccessful, or an explicit call to setLoggedOut was made.
const AUTH_LOGGED_OUT = "loggedOut";

// An attempt is underway to log in:
const AUTH_LOGGING_IN = "loggingIn";

// An authentication attempt succeeded:
const AUTH_LOGGED_IN = "loggedIn";

// (2) authIdentityString: a string representation of the identity last sent
// in a call to the authentication endpoint. Its format is as follows (with
// lowercase denoting literal text, uppercase denoting a variable value). For
// a staff member logging in: "staff:USERNAME". For a non-staff-member logging
// in: "IDENTITY_TYPE:IDENTITY" (e.g., "email_address:someone@example.com" or
// "identifying_code:INVITATION_CODE".) For an authentication check of a token
// or session cookie: null

// Pending calls to check authentication or log in will set identityString and
// will keep their own copy of it; if any other subsequent call changes it
// before the first call returns, the first call will see that its
// identityString no longer matches and its return value will be ignored as
// obsolete.

// Calls to refresh token will leave identityString unchanged; upon completion
// the call's result will be silently discarded if there is a mismatch.

// (3) requirementsCheck: set to a single-entry object, with entry
// requirementsIdentityString (same format as authIdentityString except never
// null and never beginning "staff:"), when a request is made to the server to
// determine the requirements needed for logging in as a specific person. Upon
// completion, the object will have added to it a "complete" entry, set to
// true, together with a "result" entry (object with the requirements
// as returned by the server, or null in case of an error), and, if an error
// occured and "result" is null, there will also be a "failure" entry (an
// object with "errorMessage" and "errorDetails" entries).

// (4) authenticationResult: null except when the status is AUTH_LOGGED_IN,
//     in which case it has entries
//           details: raw object returned by api call
//           staffUsername: set to the staff username if authenticated as staff
//           id: set to the person's id, if authenticated as external person
//           token: token to use in subsequent api calls
//           tokenTime: time that token was returned

// (5) authenticationFailure: null except after a failed login attempt, in which
// case is an object with entries errorMessage and errorDetails.

// (6) codeByEmail: null except after a request to send an email verification or
// password reset code, in which case is an object with entries
// emailAddress and exactly one of "sending" (true), "sent" (true), or
// "failure" (object with errorMessage / errorDetails entries).

// API calls to log in or check authentication may contain the following entries
// in addition to "data":
//   allowStaff (boolean)
//   allowExternal (boolean)
//   purpose: always set (by the action that invokes the API call), to one
//            of AUTH_LOGGING_IN, AUTH_CHECKING, or AUTH_REFRESHING_TOKEN
//            (the first two correspond to the new authenticationStatus
//            value that will be set; the last is the constant defined
//            below and signifies that only a token refresh is being done)
//   identityString: always set (by the action that invokes the API call)
//            to what the API call should set in and/or compare to the
//            authIdentityString entry in the state.

const AUTH_REFRESHING_TOKEN = "refreshingToken";

const authApiCall = createAsyncThunk(
  "auth/apiCall",
  apiPayloadCreator(
    ["auth", "/authentication"],
    "get",
    (success, arg, thunkAPI) => {
      const { allowStaff, allowExternal } = arg;

      // Authentication can return "success" for limited purposes such as
      // reporting that a password change is required or a new profile needs
      // to be created. Currently we always check requirements first and
      // send any required new-password or profile-creation information
      // along with the initial login request, so we translate such responses
      // into failures.
      if (success.unknown_email_verified && !success.person_id)
        return thunkAPI.rejectWithValue({
          message:
            "Profile not found, and option to create a new " +
            "profile was not included in the authentication request",
          details: {
            missingProfile: true,
            result: success,
          },
        });

      if (!success.token)
        return thunkAPI.rejectWithValue({
          message: success.password_change_required
            ? "Password change is required, and new password was not specified"
            : "Authentication succeeded with temporary credentials only, " +
              "insufficient for general-purpose access",
          details: {
            temporaryAuthenticationOnly: true,
            passwordChangeRequired: success.password_change_required,
            result: success,
          },
        });

      if (allowStaff && success.staff_username) return success;
      if (allowExternal && success.person_id) return success;

      return thunkAPI.rejectWithValue({
        message: "Unexpected authentication type",
        details: { allowed: { allowStaff, allowExternal }, got: success },
      });
    }
  )
);

const codeApiCall = createAsyncThunk(
  "auth/codeCall",
  apiPayloadCreator(["auth", "/authentication/send-verification-code"], "post")
);

const requirementsApiCall = createAsyncThunk(
  "auth/requirementsCall",
  apiPayloadCreator(["auth", "/authentication/requirements/external"], "post")
);

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    authenticationStatus: AUTH_UNINITIALIZED,
    requirementsCheck: null,
    authIdentityString: null,
    authenticationResult: null,
    authenticationFailure: null,
    codeByEmail: null,
  },

  reducers: {
    setLoggedOut: (state) => {
      state.authenticationStatus = AUTH_LOGGED_OUT;
      state.requirementsCheck = null;
      state.authIdentityString = null;
      state.authenticationResult = null;
      state.authenticationFailure = null;
      state.codeByEmail = null;
      window.sessionStorage.token = "";
    },
  },
  extraReducers: {
    [authApiCall.pending]: (state, action) => {
      const { purpose, identityString } = action.meta.arg;

      if (purpose === AUTH_REFRESHING_TOKEN) return;
      state.authenticationStatus = purpose;
      state.authenticationResult = null;
      state.authenticationFailure = null;
      state.authIdentityString = identityString;
    },

    [codeApiCall.pending]: (state, action) => {
      const { email: emailAddress } = action.meta.arg.data;
      state.codeByEmail = { emailAddress, sending: true };
    },

    [requirementsApiCall.pending]: (state, action) => {
      const { identityString } = action.meta.arg;
      state.requirementsCheck = { requirementsIdentityString: identityString };
    },

    [authApiCall.fulfilled]: (state, action) => {
      const { purpose, identityString } = action.meta.arg;
      // Discard delayed response to old request
      const expectedStatus =
        purpose === AUTH_REFRESHING_TOKEN ? AUTH_LOGGED_IN : purpose;
      if (state.authenticationStatus !== expectedStatus) return;
      if (identityString !== state.authIdentityString) return;

      state.authenticationStatus = AUTH_LOGGED_IN;
      state.authenticationFailure = null;
      state.authenticationResult = {
        details: action.payload,
        token: action.payload.token,
        tokenTime: Date.now(),
      };
      if (action.payload.staff_username)
        state.authenticationResult.staffUsername =
          action.payload.staff_username;
      else if (action.payload.person_id)
        state.authenticationResult.id = action.payload.person_id;
      else {
        state.authenticationStatus = AUTH_LOGGED_OUT;
        state.authenticationFailure = {
          errorMessage: "Internal error - unexpected payload",
          errorDetails: {
            payload: action.payload,
            identityString,
          },
        };
        state.authenticationResult = null;
      }
      window.sessionStorage.token = state.authenticationResult
        ? state.authenticationResult.token ?? ""
        : "";
    },

    [codeApiCall.fulfilled]: (state, action) => {
      const { email: emailAddress } = action.meta.arg.data;
      // Discard delayed response to old request
      if (emailAddress !== state.codeByEmail.emailAddress) return;
      state.codeByEmail.sending = false;
      state.codeByEmail.sent = true;
      state.codeByEmail.error = null;
    },

    [requirementsApiCall.fulfilled]: (state, action) => {
      const { identityString } = action.meta.arg;
      // Discard delayed response to old request
      if (
        !state.requirementsCheck ||
        state.requirementsCheck.requirementsIdentityString !== identityString
      )
        return;
      state.requirementsCheck.complete = true;
      state.requirementsCheck.result = action.payload;
    },

    [authApiCall.rejected]: (state, action) => {
      const { purpose, identityString } = action.meta.arg;
      if (purpose === AUTH_REFRESHING_TOKEN) return;
      // Discard delayed response to old request
      if (state.authenticationStatus !== purpose) return;
      if (identityString !== state.authIdentityString) return;
      state.authenticationStatus = AUTH_LOGGED_OUT;
      state.authenticationResult = null;
      window.sessionStorage.token = "";
      state.authenticationFailure =
        purpose === AUTH_CHECKING
          ? null
          : {
              errorMessage: action.payload.message
                ? "Error signing in: " + action.payload.message
                : "Authentication failed",
              errorDetails: action.payload.details,
            };
    },

    [codeApiCall.rejected]: (state, action) => {
      const { email: emailAddress } = action.meta.arg.data;
      // Discard delayed response to old request
      if (emailAddress !== state.codeByEmail.emailAddress) return;
      state.codeByEmail.sending = false;
      state.codeByEmail.failure = {
        errorMessage:
          "Failed to send verification code: " +
          (action.payload.message || "Unknown error"),
        errorDetails: action.payload.details,
      };
    },
    [requirementsApiCall.rejected]: (state, action) => {
      const { identityString } = action.meta.arg;
      // Discard delayed response to old request
      if (
        !state.requirementsCheck ||
        state.requirementsCheck.requirementsIdentityString !== identityString
      )
        return;
      state.requirementsCheck.complete = true;
      state.requirementsCheck.result = null;
      state.requirementsCheck.failure = {
        errorMessage:
          "Failed to check account information: " +
          (action.payload.message || "Unknown error"),
        errorDetails: action.payload.details,
      };
    },
  },
});

export const { setLoggedOut } = authSlice.actions;

// Selector for auth state. Not exported; other components use
// selectAuthToken (for passing to api) or useAuthDetails.
const selectAuth = (state) => state.auth;
export const selectAuthToken = (state) => {
  const auth = selectAuth(state);
  return auth.authenticationResult
    ? auth.authenticationResult.token ?? null
    : null;
};

// Constructor of the identityString auth entry from the data that will be
// sent to the authentication endpoint for external user authentication.
export const buildIdentityString = ({ identity_type, identity }) =>
  `${identity_type}:${identity}`;

export default authSlice.reducer;

export const checkAuth = (token, allow) => (dispatch) => {
  return dispatch(
    authApiCall({
      path: "/check",
      authToken: token,
      ...allow,
      purpose: AUTH_CHECKING,
      identityString: null,
    })
  );
};
export const refreshToken = () => (dispatch, getState) => {
  const auth = selectAuth(getState());
  const token = auth.authenticationResult
    ? auth.authenticationResult.token
    : null;
  return dispatch(
    authApiCall({
      path: "/check",
      authToken: token,
      purpose: AUTH_REFRESHING_TOKEN,
      identityString: auth.authIdentityString,
    })
  );
};

export const checkSessionAuth = (allow) => (dispatch) => {
  return dispatch(
    authApiCall({
      path: "/check",
      ...allow,
      purpose: AUTH_CHECKING,
      identityString: null,
    })
  );
};

export const initiateStaffLogin = (username, credentials) => (dispatch) => {
  return dispatch(
    authApiCall({
      path: "/login/staff",
      method: "post",
      data: { staff_credentials: credentials },
      allowStaff: true,
      purpose: AUTH_LOGGING_IN,
      identityString: `staff:${username}`,
    })
  );
};

export const initiateExternalLogin = (data) => (dispatch) => {
  return dispatch(
    authApiCall({
      path: "/login/external",
      method: "post",
      data: data,
      allowExternal: true,
      purpose: AUTH_LOGGING_IN,
      identityString: buildIdentityString(data),
    })
  );
};

export const initiateCodeRequest = (emailAddress) => (dispatch) => {
  return dispatch(
    codeApiCall({
      method: "post",
      data: { email: emailAddress },
    })
  );
};

export const initiateRequirementsCheck = (identitySpec) => (dispatch) => {
  return dispatch(
    requirementsApiCall({
      method: "post",
      data: identitySpec,
      identityString: buildIdentityString(identitySpec),
    })
  );
};

// Auth: React component that can be used as a wrapper around the main part of
// the app, that needs to know the authentication state.

// If the authentication state is uninitialized or checking existing
// cookie/token, Auth will not render its children, but will instead
// render null, or the value of the optional initializingElement prop
// if provided. In addition, if the state is uninitialized, it will dispatch
// an authentication check call.

// If the authentication state is AUTH_LOGGED_IN but the type of login
// doesn't match what the children desire (as expressed by allowStaff and/or
// allowExternal props passed to Auth), or has an out-of-date token
// (such as if browser session was just resumed after a long hiatus),
// Auth dispatches a call to reset the state to "logged out", and renders the
// value of the optional resettingElement prop, or null if not provided.

// Otherwise, renders its children, which can then call useAuthDetails
// to determine authentication status, and these children can be assured
// that the status is one of AUTH_LOGGED_OUT, AUTH_LOGGING_IN, or
// AUTH_LOGGED_IN, and, if AUTH_LOGGED_IN, is compatible with the allowStaff /
// allowExternal props passed to Auth.

// In addition, if the status is AUTH_LOGGED_IN, Auth will set up a timer to
// periodically refresh the token.

export function Auth({
  allowStaff,
  allowExternal,
  children,
  initializingElement = null,
  resettingElement = null,
}) {
  const authState = useAuthenticationState();
  const dispatch = useDispatch();

  const {
    uninitialized,
    checking,
    loggedIn,
    authenticatedAsStaff,
    authenticatedAsExternal,
    authenticationResult,
    token,
    tokenTime,
  } = authState;

  // Check for inconsistencies that in theory cannot happen but this check
  // guards against future bugs in the code creeping in.
  const inconsistent = loggedIn
    ? (!authenticatedAsStaff && !authenticatedAsExternal) ||
      (authenticatedAsStaff && authenticatedAsExternal) ||
      !token
    : !!authenticationResult;

  // Check for wrong or expired authentication
  const wrongType =
    (authenticatedAsStaff && !allowStaff) ||
    (authenticatedAsExternal && !allowExternal);

  const expired =
    loggedIn && tokenTime < Date.now() - 2 * AUTH_REFRESH_INTERVAL;

  const mustReset = wrongType || expired || inconsistent;

  useEffect(() => {
    if (uninitialized) {
      const token = window.sessionStorage.token;
      dispatch(
        token
          ? checkAuth(token, { allowStaff, allowExternal })
          : checkSessionAuth({ allowStaff, allowExternal })
      );
    }
    if (mustReset) dispatch(setLoggedOut());
  }, [uninitialized, mustReset, dispatch, allowStaff, allowExternal]);

  const authenticated = loggedIn && !wrongType && !expired && !inconsistent;

  useEffect(() => {
    let timerId = null;
    if (authenticated) {
      timerId = window.setInterval(
        () => dispatch(refreshToken()),
        AUTH_REFRESH_INTERVAL
      );
    }
    return () => {
      if (timerId !== null) window.clearInterval(timerId);
    };
  }, [authenticated, dispatch]);

  return mustReset
    ? resettingElement ?? null
    : uninitialized || checking
    ? initializingElement
    : children;
}

// Hook to provide information about the authentication state in a format
// more suitable for consuming components to use (such as separate flags
// to indicate conditions such as loggedIn or authenticatedAsStaff), whereas
// the structure of the raw state is more suitable for ensuring data
// consistency and easier manipulation by API callbacks (such as setting a
// single authenticationStatus variable to a particular value rather than
// having to set and clear a bunch of independent bookean flags).

// Returns the authentication state with the following modifications:

// Instead of authenticationStatus, exactly one of the following boolean
// flags will be set: uninitialized, checking, loggedOut, loggingIn, loggedIn.

// In addition to authenticationResult, the authenticationResult entries
// staffUsername, id, token, and tokenTime are present at the main level.

// Additional boolean flags authenticatedAsStaff and authenticatedAsExternal
// are present.

export function useAuthenticationState() {
  const {
    authenticationStatus: status,
    authenticationResult: result,
    authIdentityString,
    requirementsCheck,
    authenticationFailure,
    codeByEmail,
  } = useSelector(selectAuth);
  const authenticatedAsStaff =
    status === AUTH_LOGGED_IN && result && result.staffUsername !== undefined;

  const authenticatedAsExternal =
    status === AUTH_LOGGED_IN && result && result.id !== undefined;

  const { staffUsername, id, token, tokenTime } = result || {};

  return {
    uninitialized: status === AUTH_UNINITIALIZED,
    checking: status === AUTH_CHECKING,
    loggedOut: status === AUTH_LOGGED_OUT,
    loggingIn: status === AUTH_LOGGING_IN,
    loggedIn: status === AUTH_LOGGED_IN,
    authenticatedAsStaff,
    authenticatedAsExternal,
    authIdentityString,
    requirementsCheck,
    id,
    staffUsername,
    token,
    tokenTime,
    authenticationResult: result,
    authenticationFailure,
    codeByEmail,
  };
}
